import { throttle } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dimensions, ScaledSize } from 'react-native';

type Breakpoint<T extends string | number> = { key: T; width: number };

export const useScreenBreakpoints = <T extends string | number>(
  breakpoints: Breakpoint<T>[],
  onChangeThrottle: number = 250,
): T | null => {
  const getBreakpointKey = useCallback(
    (width: number): T | null =>
      breakpoints.reduce<Breakpoint<T> | null>(
        (acc, breakpoint) =>
          breakpoint.width <= width && (!acc || acc.width < breakpoint.width)
            ? breakpoint
            : acc,
        null,
      )?.key || null,
    [breakpoints],
  );

  const initBreakpointKey = useMemo(
    () => getBreakpointKey(Dimensions.get('window').width),
    [getBreakpointKey],
  );

  const [breakpointKey, setBreakpointKey] = useState(initBreakpointKey);

  const onChange = useMemo(
    () =>
      throttle(
        ({ window: { width } }: { window: ScaledSize }) =>
          setBreakpointKey(getBreakpointKey(width)),
        onChangeThrottle,
      ),
    [getBreakpointKey, onChangeThrottle],
  );

  useEffect(() => {
    const listener = Dimensions.addEventListener('change', onChange);
    return () => {
      listener.remove();
    };
  }, [onChange]);

  return breakpointKey;
};
